import * as React from "react";
import { cva, VariantProps } from "class-variance-authority";

import { cn } from "@src/lib/utils/cn";
import { Slot } from "@radix-ui/react-slot";
import { textV4Variants } from "@src/lib/ui/Text";
import { Loader2 } from "lucide-react";

const buttonVariants = cva(
  // Invalid controls marked with aria-invalid get a status-error ring.
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg transition-all select-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-4 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:shadow-[0_0_0_1px_var(--color-status-error-border)] [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-action-primary text-action-primary-foreground shadow-default disabled:shadow-none hover:bg-action-primary-hover active:bg-action-primary-active focus-visible:ring-brand-border",
        destructive:
          "bg-action-destructive text-action-destructive-foreground shadow-none hover:bg-action-destructive-hover active:bg-action-destructive-active focus-visible:ring-action-destructive focus-visible:ring-offset-2",
        outline:
          "bg-bg-primary text-text-primary shadow-default hover:bg-state-hover aria-expanded:bg-state-hover focus-visible:ring-brand-border",
        secondary:
          "bg-action-secondary text-action-secondary-foreground shadow-none hover:bg-action-secondary-hover active:bg-action-secondary-active aria-expanded:bg-action-secondary-hover focus-visible:ring-brand-border",
        ghost:
          "bg-transparent text-text-secondary shadow-none hover:bg-state-hover active:bg-state-pressed aria-expanded:bg-state-hover focus-visible:ring-brand-border",
        link: "text-action-primary underline-offset-4 hover:underline shadow-none focus-visible:ring-brand-border",
        ai: "bg-gradient-to-r from-[#BD82FA] via-[#FE508F] to-[#FB99A6] text-background hover:opacity-90 focus-visible:ring-purple-4 shadow-none",
        purple:
          "bg-purple-9 hover:bg-purple-10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-purple-4 text-white",
        blur: "bg-white/5 backdrop-blur-[12.5px] text-white hover:bg-white/10 shadow-none focus-visible:ring-white/30",
      },
      size: {
        default:
          "h-9 px-4 py-2 gap-1.5 has-data-[icon=inline-start]:pl-3 has-data-[icon=inline-end]:pr-3 [&_svg:not([class*='size-'])]:size-4",
        xs: "h-6 px-[10px] gap-[6px] rounded-[8px] has-data-[icon=inline-start]:pl-2 has-data-[icon=inline-end]:pr-2 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 px-2.5 gap-1 has-data-[icon=inline-start]:pl-2 has-data-[icon=inline-end]:pr-2 [&_svg:not([class*='size-'])]:size-3",
        md: "h-8 pl-2.5 pr-3 gap-1.5 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-10 px-4 gap-2 has-data-[icon=inline-start]:pl-3 has-data-[icon=inline-end]:pr-3 [&_svg:not([class*='size-'])]:size-4",
        icon: "h-9 w-9 gap-1 [&_svg:not([class*='size-'])]:size-4",
        "icon-xs": "h-6 w-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "h-7 w-7 [&_svg:not([class*='size-'])]:size-4",
        "icon-md": "h-8 w-8 [&_svg:not([class*='size-'])]:size-4",
        "icon-lg": "h-10 w-10 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    compoundVariants: [
      {
        variant: ["default", "destructive", "outline", "secondary", "ghost"],
        className:
          "rounded-full active:translate-y-0 active:scale-[0.96] focus-visible:ring-[3px]",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  dataTestId?: string;
  loading?: boolean;
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      dataTestId,
      loading,
      loadingText,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          textV4Variants({
            variant: (() => {
              const compact =
                size === "sm" || size === "xs" ? "helper" : "compact";
              const role = ["secondary", "outline", "ghost"].includes(
                variant ?? "default",
              )
                ? "description"
                : "title";
              return `${compact}-${role}` as const;
            })(),
          }),
          "transition-transform duration-75 ease-out",
          loading ? "opacity-50 cursor-not-allowed" : "",
          className,
        )}
        ref={ref}
        data-testid={dataTestId}
        {...props}
        disabled={props.disabled || loading}
      >
        {loading ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {loadingText}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
